import once from 'lodash/once';
import some from 'lodash/some';
import map from 'lodash/map';
import log from '../helpers/log';
import logLevels from '../helpers/logLevels';
import staticConfigFactory from '../helpers/StaticConfig';

const StaticConfig = staticConfigFactory();

const PATH_TO_RELEASE_NOTES = '/opentok/webrtc/docs/js/release-notes.html';
const staticConfig = StaticConfig.onlyLocal();
const logging = log('setLogLevel');

const BANNER_HEADER = `
 ██████╗ ██████╗ ███████╗███╗   ██╗████████╗ ██████╗ ██╗  ██╗
██╔═══██╗██╔══██╗██╔════╝████╗  ██║╚══██╔══╝██╔═══██╗██║ ██╔╝
██║   ██║██████╔╝█████╗  ██╔██╗ ██║   ██║   ██║   ██║█████╔╝
██║   ██║██╔═══╝ ██╔══╝  ██║╚██╗██║   ██║   ██║   ██║██╔═██╗
╚██████╔╝██║     ███████╗██║ ╚████║   ██║   ╚██████╔╝██║  ██╗
 ╚═════╝ ╚═╝     ╚══════╝╚═╝  ╚═══╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝`;

const printHeader = once(() => {
  logging.info(`%c${BANNER_HEADER}`, 'font-family: "Fire Code", Courier, "Courier New", Consolas, "DejaVu Sans Mono"');
  logging.info(`OpenTok JS SDK ${staticConfig.version}`);
  logging.info(`Release notes: ${staticConfig.websiteUrl}${PATH_TO_RELEASE_NOTES}`);
  logging.info(`Known issues: ${staticConfig.websiteUrl}${PATH_TO_RELEASE_NOTES}l#knownIssues`);
});

function setLogLevel(level) {
  if (level === undefined || (!some(logLevels, { priority: level }) && level !== 0)) {
    const validLevelDescription = map(logLevels, (x, key) => `OT.${key.toUpperCase()} = ${x.priority}`).join(', ');
    logging.error(`level must be one of: OT.NONE = 0, ${validLevelDescription}`);
    logging.warn('Using OT.WARN instead');
    level = logLevels.warn.priority; // eslint-disable-line no-param-reassign
  }

  log.setLogLevel(level);

  if (level >= logLevels.debug.priority) {
    printHeader();
  }
  const link = 'https://www.npmjs.com/package/debug#browser-support';

  logging.info(`OpenTok JS SDK now uses the debug library. More info at ${link}`);
  logging.info(`Changed log level to: ${level}`);

  return level;
}

// set default level
setLogLevel(staticConfig.debug ? logLevels.debug.priority : logLevels.warn.priority);

export default setLogLevel;

/**
  * Sets the API log level.
  * <p>
  * Calling <code>OT.setLogLevel()</code> sets the log level for runtime log messages that
  * are the OpenTok library generates. The default value for the log level is
  * <code>OT.ERROR</code>.
  * </p>
  * <p>
  * The OpenTok JavaScript library displays log messages in the debugger console (such as
  * Firebug), if one exists.
  * </p>
  * <p>
  * The following example logs the session ID to the console, by calling <code>OT.log()</code>.
  * The code also logs an error message when it attempts to publish a stream before you have
  * connected to the session (by calling <code>Session.connect()</code>).
  * </p>
  * <pre>
  * OT.setLogLevel(OT.LOG);
  * session = OT.initSession(sessionId);
  * OT.log(sessionId);
  * publisher = OT.initPublisher("publishContainer");
  * session.publish(publisher);
  * </pre>
  *
  * @param {Number} logLevel The degree of logging desired by the developer:
  *
  * <p>
  * <ul>
  *   <li>
  *     <code>OT.NONE</code> &#151; API logging is disabled.
  *   </li>
  *   <li>
  *     <code>OT.ERROR</code> &#151; Logging of errors only.
  *   </li>
  *   <li>
  *     <code>OT.WARN</code> &#151; Logging of warnings and errors.
  *   </li>
  *   <li>
  *     <code>OT.INFO</code> &#151; Logging of other useful information, in addition to
  *     warnings and errors.
  *   </li>
  *   <li>
  *     <code>OT.LOG</code> &#151; Logging of <code>OT.log()</code> messages, in addition
  *     to OpenTok info, warning,
  *     and error messages.
  *   </li>
  *   <li>
  *     <code>OT.DEBUG</code> &#151; Fine-grained logging of all API actions, as well as
  *     <code>OT.log()</code> messages.
  *   </li>
  * </ul>
  * </p>
  *
  * @name OT.setLogLevel
  * @memberof OT
  * @function
  * @see <a href="#log">OT.log()</a>
  */

/**
  * Sends a string to the debugger console. However, the function only logs to the console
  * if you have set the log level to <code>OT.LOG</code> or <code>OT.DEBUG</code>,
  * by calling <code>OT.setLogLevel(OT.LOG)</code> or <code>OT.setLogLevel(OT.DEBUG)</code>.
  *
  * @param {String} message The string to log.
  *
  * @name OT.log
  * @memberof OT
  * @function
  * @see <a href="#setLogLevel">OT.setLogLevel()</a>
  */
